import React, { useState } from "react";
import {
    Server,
    ArrowRight,
    Loader2,
    Check,
    AlertCircle,
    Calendar,
    RefreshCw,
    Database,
    Trash2,
} from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useChatContext } from "../ChatContext";
import { useWorkflow } from "../../context/WorkflowContext";
import { useWorkflowGeneration } from "../../context/WorkflowGenerationContext";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import PlanRestrictionAlert from "./PlanRestrictionAlert";
import { useConfirm } from "../../hooks/useConfirm";

function EnvironmentsPanel({ isEnvironmentsFeatureEnabled }) {
    const { t } = useTranslation();
    const {
        environments,
        fetchEnvironments,
        selectedEnvironment,
        setSelectedEnvironment,
        productionVersionId,
    } = useChatContext();
    const { workflow, refetchWorkflow } = useWorkflow();
    const { missingSecretWarnings: contextSecretWarnings, setMissingSecretWarnings: setContextSecretWarnings } =
        useWorkflowGeneration();
    const [promotingFrom, setPromotingFrom] = useState(null);
    const [promotingTo, setPromotingTo] = useState(null);
    const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false);

    const { call: promoteToEnvironmentApi, isLoading: isPromoting } =
        useBluelagoon({
            method: "promoteToEnvironment",
        });

    const { call: rollbackWorkflowStepApi } = useBluelagoon({
        method: "rollbackWorkflowStep",
    });

    const { call: copyProductionDataApi, isLoading: isCopyingData } = useBluelagoon({
        method: "copyProductionDatabaseToEnvironment",
    });

    const { call: deleteEnvironmentApi } = useBluelagoon({
        method: "deleteWorkflowEnvironment",
    });

    const [syncingEnvironmentId, setSyncingEnvironmentId] = useState(null);
    const [copyDataTargetEnv, setCopyDataTargetEnv] = useState(null);
    const [isCopyDataDialogOpen, setIsCopyDataDialogOpen] = useState(false);
    const [deletingEnvironmentId, setDeletingEnvironmentId] = useState(null);
    const [confirm, ConfirmDialog] = useConfirm();

    // Check if an environment is behind production
    const isEnvironmentBehind = (environment) => {
        if (!productionVersionId) return false;
        const isProduction = environment.type === "PRODUCTION" ||
            environment.name?.toLowerCase() === "production" ||
            environment.name?.toLowerCase() === "prod";
        if (isProduction) return false;
        return environment.currentWorkflowStepVersionId !== productionVersionId;
    };

    // Sync environment to match production using rollback
    const handleSyncToProduction = async (environment) => {
        const stepId = workflow?.steps?.[0]?.id;
        if (!stepId || !productionVersionId) return;

        setSyncingEnvironmentId(environment.id);
        try {
            const result = await rollbackWorkflowStepApi({
                stepId,
                versionId: productionVersionId,
                environmentName: environment.name,
            });

            if (result?.status === 200) {
                toast.success(
                    t("environments.syncSuccess") || "Successfully synced to production"
                );
                setContextSecretWarnings(
                    result.data?.missingSecretWarnings || []
                );
                fetchEnvironments();
                refetchWorkflow();
            } else {
                throw new Error(
                    result?.message ||
                        t("environments.syncError") ||
                        "Failed to sync to production"
                );
            }
        } catch (error) {
            console.error("Error syncing to production:", error);
            toast.error(
                error.message ||
                    t("environments.syncError") ||
                    "Failed to sync to production"
            );
        } finally {
            setSyncingEnvironmentId(null);
        }
    };

    const handleCopyProductionData = async () => {
        if (!copyDataTargetEnv || !workflow?.id) return;

        try {
            const result = await copyProductionDataApi({
                workflowId: workflow.id,
                targetEnvironmentId: copyDataTargetEnv.id,
            });

            if (result?.success) {
                toast.success(
                    result.message ||
                        t("environments.copyDataSuccess") ||
                        "Successfully copied production data"
                );
                setIsCopyDataDialogOpen(false);
                setCopyDataTargetEnv(null);
            } else {
                throw new Error(
                    result?.error ||
                        t("environments.copyDataError") ||
                        "Failed to copy production data"
                );
            }
        } catch (error) {
            console.error("Error copying production data:", error);
            const errorMessage =
                error?.error || error?.message ||
                t("environments.copyDataError") ||
                "Failed to copy production data";
            toast.error(errorMessage);
        }
    };

    const handleDeleteEnvironment = async (environment) => {
        // Production envs are never user-deletable. The icon is hidden for
        // production, but re-check here as a defense in depth.
        const isProduction =
            environment.type === "PRODUCTION" ||
            environment.name?.toLowerCase() === "production" ||
            environment.name?.toLowerCase() === "prod";
        if (isProduction) return;

        // useConfirm — Promise resolves on confirm, rejects on cancel.
        // Uses the pre-existing `environments.delete.*` translation block
        // (title / confirm / success / error) so we don't duplicate strings.
        try {
            await confirm({
                title:
                    t("environments.delete.title") ||
                    "Delete this environment?",
                description:
                    t("environments.delete.confirm", {
                        name: environment.name,
                    }) ||
                    `Are you sure you want to delete the "${environment.name}" environment? This action cannot be undone.`,
                confirmText: t("common.delete") || "Delete",
                cancelText: t("common.cancel") || "Cancel",
                isDestructive: true,
            });
        } catch {
            return;
        }

        setDeletingEnvironmentId(environment.id);
        try {
            const result = await deleteEnvironmentApi({
                workflowId: workflow.id,
                environmentId: environment.id,
            });
            if (result?.success) {
                toast.success(
                    t("environments.delete.success") ||
                        "Environment deleted successfully"
                );
                // If we just deleted the currently-selected env, fall back
                // to production (represented as `null` per the rest of the
                // ChatContext flow). Otherwise the UI keeps showing the now-
                // gone env, which leaves component fetches keyed against an
                // environment that no longer exists.
                if (selectedEnvironment === environment.name) {
                    setSelectedEnvironment(null);
                }
                fetchEnvironments();
            } else {
                throw new Error(
                    result?.error?.message ||
                        t("environments.delete.error") ||
                        "Failed to delete environment"
                );
            }
        } catch (error) {
            toast.error(
                error.message ||
                    t("environments.delete.error") ||
                    "Failed to delete environment"
            );
        } finally {
            setDeletingEnvironmentId(null);
        }
    };

    const handlePromoteClick = (sourceEnvironment) => {
        setPromotingFrom(sourceEnvironment);
        setIsPromoteDialogOpen(true);
    };

    const handleConfirmPromote = async () => {
        if (!promotingFrom || !promotingTo || !workflow?.id) {
            return;
        }

        try {
            // Find source environment ID
            const sourceEnv = environments.find(
                (e) => e.name === promotingFrom.name
            );
            const sourceEnvironmentId = sourceEnv?.id || null;

            // Find target environment ID
            // For production, it might not exist as a WorkflowEnvironment
            // Try to find it in environments first
            const isTargetProduction =
                promotingTo.name?.toLowerCase() === "production" ||
                promotingTo.name?.toLowerCase() === "prod" ||
                promotingTo.type === "PRODUCTION";

            let targetEnvironmentId = null;
            if (isTargetProduction) {
                //  production does not exist as a WorkflowEnvironment
                targetEnvironmentId = "PRODUCTION";
            } else {
                // For non-production environments, find the target environment
                const targetEnv = environments.find(
                    (e) => e.name === promotingTo.name
                );
                if (!targetEnv?.id) {
                    toast.error(
                        t("environments.targetEnvironmentNotFound") ||
                            "Target environment not found"
                    );
                    return;
                }
                targetEnvironmentId = targetEnv.id;
            }

            const result = await promoteToEnvironmentApi({
                workflowId: workflow.id,
                targetEnvironmentId: targetEnvironmentId,
                sourceEnvironmentId: sourceEnvironmentId,
            });

            if (result?.success) {
                toast.success(
                    result.message ||
                        t("environments.promoteSuccess") ||
                        "Successfully promoted to environment"
                );

                setContextSecretWarnings(
                    result.missingSecretWarnings || []
                );

                // Refresh environments and workflow
                fetchEnvironments();
                refetchWorkflow();
                setIsPromoteDialogOpen(false);
                setPromotingFrom(null);
                setPromotingTo(null);
            } else {
                throw new Error(
                    result?.error?.message ||
                        t("environments.promoteError") ||
                        "Failed to promote environment"
                );
            }
        } catch (error) {
            console.error("Error promoting environment:", error);
            toast.error(
                error.message ||
                    t("environments.promoteError") ||
                    "Failed to promote environment"
            );
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return null;
        }
    };

    // Get available target environments (exclude the source environment, but always include production)
    const getAvailableTargetEnvironments = () => {
        // Get non-source environments
        const availableEnvs = promotingFrom
            ? environments.filter((env) => env.name !== promotingFrom.name)
            : environments;

        // Check if production exists in the environments list
        const productionInEnvironments = environments.find(
            (env) =>
                env.name?.toLowerCase() === "production" ||
                env.name?.toLowerCase() === "prod" ||
                env.type === "PRODUCTION"
        );

        // Always include production as an option, even if it's not in the environments list
        // If production exists in environments and is not the source, it's already in availableEnvs
        // If production doesn't exist in environments, add it as an option
        // If production is the source, still show it as an option (you can promote to production)
        const productionOption = productionInEnvironments
            ? null // Already in the list
            : {
                  id: null, // Production might not have an ID if it's not a WorkflowEnvironment
                  name: "production",
                  type: "PRODUCTION",
              };

        // If production is the source, we still want to show it as a target option
        // (though typically you wouldn't promote from production to production)
        const isSourceProduction =
            promotingFrom &&
            (promotingFrom.name?.toLowerCase() === "production" ||
                promotingFrom.name?.toLowerCase() === "prod" ||
                promotingFrom.type === "PRODUCTION");

        if (productionOption) {
            // Production doesn't exist in environments, add it to the list
            return [productionOption, ...availableEnvs];
        } else if (isSourceProduction) {
            // Production exists but is the source, add it back to the list
            return [productionInEnvironments, ...availableEnvs];
        }

        return availableEnvs;
    };

    return (
        <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Server className="w-6 h-6 text-blue-600" />
                        {t("environments.title") || "Environments"}
                    </h1>
                    <p className="text-sm text-gray-600 mt-2">
                        {t("environments.description") ||
                            "Manage and promote versions across your environments"}
                    </p>
                </div>

                {/* Plan Restriction Alert */}
                {!isEnvironmentsFeatureEnabled && (
                    <PlanRestrictionAlert
                        titleKey="environments.upgradeRequired"
                        titleFallback="Upgrade Required"
                        descriptionKey="environments.environmentsUpgradeMessage"
                        descriptionFallback="Environments are available on Creator plan and above. Upgrade your plan to enable this feature."
                        variant="inline"
                    />
                )}

                {/* Missing Secret Warnings */}
                {contextSecretWarnings.length > 0 && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <div>
                            <p className="font-medium">
                                {t(
                                    "common.missingSecretsWarning",
                                    "This version references secrets that are missing or have no value: {{secretNames}}. The build may fail or behave unexpectedly.",
                                    {
                                        secretNames: contextSecretWarnings
                                            .map((w) => w.secretName)
                                            .join(", "),
                                    }
                                )}
                            </p>
                        </div>
                    </div>
                )}

                {/* Environments List */}
                <div className="space-y-4">
                    {environments && environments.length > 0 ? (
                        environments.map((environment) => {
                            const isSelected =
                                selectedEnvironment === environment.name;
                            const isProduction =
                                environment.type === "PRODUCTION" ||
                                environment.name === "production";
                            const isBehind = isEnvironmentBehind(environment);
                            const isSyncing = syncingEnvironmentId === environment.id;

                            return (
                                <div
                                    key={environment.id}
                                    className={`bg-white rounded-lg shadow-sm border-2 ${
                                        isSelected
                                            ? "border-blue-500"
                                            : "border-gray-200"
                                    } p-6`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {environment.name}
                                                </h3>
                                                {isSelected && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                        {t(
                                                            "environments.current"
                                                        ) || "Current"}
                                                    </span>
                                                )}
                                                {isProduction && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                        {t(
                                                            "environments.production"
                                                        ) || "Production"}
                                                    </span>
                                                )}
                                                {environment.type &&
                                                    environment.type !==
                                                        "PRODUCTION" &&
                                                    environment.type !==
                                                        "CUSTOM" && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                            {environment.type}
                                                        </span>
                                                    )}
                                                {isBehind && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                                        {t("environments.behind") || "Behind"}
                                                    </span>
                                                )}
                                            </div>

                                            {environment.customDomain && (
                                                <p className="text-sm text-gray-600 mb-2">
                                                    <span className="font-medium">
                                                        {t(
                                                            "environments.domain"
                                                        ) || "Domain:"}
                                                    </span>{" "}
                                                    {environment.customDomain}
                                                </p>
                                            )}

                                            {environment.lastPromotedAt && (
                                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                                                    <Calendar className="w-4 h-4" />
                                                    <span>
                                                        {t(
                                                            "environments.lastPromoted"
                                                        ) ||
                                                            "Last promoted:"}{" "}
                                                        {formatDate(
                                                            environment.lastPromotedAt
                                                        )}
                                                    </span>
                                                </div>
                                            )}

                                            {environment.currentWorkflowStepVersionId && (
                                                <div className="mt-2 text-xs text-gray-500">
                                                    <span className="font-medium">
                                                        {t(
                                                            "environments.version"
                                                        ) || "Version ID:"}
                                                    </span>{" "}
                                                    {environment.currentWorkflowStepVersionId.substring(
                                                        0,
                                                        8
                                                    )}
                                                    ...
                                                </div>
                                            )}
                                        </div>

                                        <div className="ml-4 flex flex-col gap-2">
                                            {/* Small trash icon, top of the
                                                action column, right-aligned
                                                so it never stretches to the
                                                column width. */}
                                            {!isProduction && (
                                                <button
                                                    onClick={() =>
                                                        handleDeleteEnvironment(
                                                            environment
                                                        )
                                                    }
                                                    disabled={
                                                        deletingEnvironmentId ===
                                                        environment.id
                                                    }
                                                    aria-label={
                                                        t("common.delete") ||
                                                        "Delete"
                                                    }
                                                    title={
                                                        t(
                                                            "environments.delete.title"
                                                        ) ||
                                                        "Delete environment"
                                                    }
                                                    className="self-end p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {deletingEnvironmentId ===
                                                    environment.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="w-4 h-4" />
                                                    )}
                                                </button>
                                            )}
                                            {isBehind && (
                                                <Button
                                                    onClick={() =>
                                                        handleSyncToProduction(
                                                            environment
                                                        )
                                                    }
                                                    disabled={isSyncing || isPromoting}
                                                    className="flex items-center gap-2"
                                                    variant="default"
                                                >
                                                    {isSyncing ? (
                                                        <>
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                            {t("common.syncing") || "Syncing..."}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <RefreshCw className="w-4 h-4" />
                                                            {t("environments.syncToProduction") || "Bring back to production"}
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                            {!isProduction && (
                                                <Button
                                                    onClick={() => {
                                                        setCopyDataTargetEnv(environment);
                                                        setIsCopyDataDialogOpen(true);
                                                    }}
                                                    disabled={isCopyingData || isPromoting}
                                                    className="flex items-center gap-2"
                                                    variant="outline"
                                                >
                                                    <Database className="w-4 h-4" />
                                                    {t("environments.copyProductionData") || "Copy Production Data"}
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() =>
                                                    handlePromoteClick(
                                                        environment
                                                    )
                                                }
                                                disabled={
                                                    isPromoting ||
                                                    isProduction ||
                                                    environment.name ===
                                                        "production"
                                                }
                                                className="flex items-center gap-2"
                                                variant="outline"
                                            >
                                                {isPromoting &&
                                                promotingFrom?.id ===
                                                    environment.id ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        {t(
                                                            "common.promoting"
                                                        ) || "Promoting..."}
                                                    </>
                                                ) : (
                                                    <>
                                                        <ArrowRight className="w-4 h-4" />
                                                        {t(
                                                            "environments.promote"
                                                        ) || "Promote"}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {isProduction && (
                                        <div className="mt-3 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                                            <p className="text-xs text-yellow-700">
                                                {t(
                                                    "environments.productionNote"
                                                ) ||
                                                    "Production environment cannot be promoted from. Use component mutations to update production directly."}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
                            <p className="text-gray-500">
                                {t("environments.noEnvironments") ||
                                    "No environments found"}
                            </p>
                        </div>
                    )}
                </div>

                {/* Delete confirmation — provided by the shared useConfirm hook */}
                <ConfirmDialog />

                {/* Promotion Dialog */}
                <Dialog
                    open={isPromoteDialogOpen}
                    onOpenChange={(open) => {
                        if (!isPromoting) {
                            setIsPromoteDialogOpen(open);
                            if (!open) {
                                setPromotingFrom(null);
                                setPromotingTo(null);
                            }
                        }
                    }}
                >
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {t("environments.promoteDialogTitle") ||
                                    "Promote to Environment"}
                            </DialogTitle>
                            <DialogDescription>
                                {promotingFrom && (
                                    <>
                                        {t(
                                            "environments.promoteDialogDescription"
                                        ) ||
                                            "Select the target environment to promote from"}{" "}
                                        <span className="font-semibold">
                                            {promotingFrom.name}
                                        </span>
                                    </>
                                )}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-3 py-4">
                            {getAvailableTargetEnvironments().map((env) => {
                                // Use name as key if id is null (for production that doesn't exist as WorkflowEnvironment)
                                const envKey = env.id || `env-${env.name}`;
                                const isSelected =
                                    promotingTo?.id === env.id ||
                                    (promotingTo?.name === env.name &&
                                        !env.id &&
                                        !promotingTo?.id);

                                return (
                                    <button
                                        key={envKey}
                                        onClick={() => setPromotingTo(env)}
                                        disabled={isPromoting}
                                        className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                                            isSelected
                                                ? "border-blue-500 bg-blue-50"
                                                : "border-gray-200 hover:border-gray-300"
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h4 className="font-semibold text-gray-900">
                                                    {env.name}
                                                </h4>
                                                {env.type && (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {env.type}
                                                    </p>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <Check className="w-5 h-5 text-blue-600" />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsPromoteDialogOpen(false);
                                    setPromotingFrom(null);
                                    setPromotingTo(null);
                                }}
                                disabled={isPromoting}
                            >
                                {t("common.cancel") || "Cancel"}
                            </Button>
                            <Button
                                onClick={handleConfirmPromote}
                                disabled={
                                    isPromoting ||
                                    !promotingFrom ||
                                    !promotingTo
                                }
                            >
                                {isPromoting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t("common.promoting") ||
                                            "Promoting..."}
                                    </>
                                ) : (
                                    <>
                                        <ArrowRight className="w-4 h-4 mr-2" />
                                        {t("environments.confirmPromote") ||
                                            "Promote"}
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Copy Production Data Dialog */}
                <Dialog
                    open={isCopyDataDialogOpen}
                    onOpenChange={(open) => {
                        if (!isCopyingData) {
                            setIsCopyDataDialogOpen(open);
                            if (!open) setCopyDataTargetEnv(null);
                        }
                    }}
                >
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {t("environments.copyDataDialogTitle") ||
                                    "Copy Production Data"}
                            </DialogTitle>
                            <DialogDescription>
                                {t("environments.copyDataDialogDescription") ||
                                    "This will copy all data from the production database to"}{" "}
                                <span className="font-semibold">
                                    {copyDataTargetEnv?.name}
                                </span>
                                .{" "}
                                <span className="text-red-600 font-medium">
                                    {t("environments.copyDataWarning") ||
                                        "This will overwrite all existing data in the target environment."}
                                </span>
                            </DialogDescription>
                        </DialogHeader>

                        <div className="py-4">
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-yellow-700">
                                        {t("environments.copyDataNote") ||
                                            "Make sure you want to replace all data in this environment with production data. This action cannot be undone."}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsCopyDataDialogOpen(false);
                                    setCopyDataTargetEnv(null);
                                }}
                                disabled={isCopyingData}
                            >
                                {t("common.cancel") || "Cancel"}
                            </Button>
                            <Button
                                onClick={handleCopyProductionData}
                                disabled={isCopyingData || !copyDataTargetEnv}
                                variant="destructive"
                            >
                                {isCopyingData ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t("common.copying") || "Copying..."}
                                    </>
                                ) : (
                                    <>
                                        <Database className="w-4 h-4 mr-2" />
                                        {t("environments.confirmCopyData") ||
                                            "Copy Data"}
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}

export default EnvironmentsPanel;
